import {
  Box,
  Button,
  Heading,
  IconButton,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tooltip,
  Tr,
} from "@chakra-ui/react";
import { useState } from "react";
import { StartlistExtendedRow } from "../../../../Api/dto/StartlistDto";
import { ArrowUp, EditIcon, WrenchIcon } from "lucide-react";
import { ArrowDown } from "lucide-react";
import { Role, useSessionState } from "../../../../Session/SessionService";

const SORT_DIRECTION_ASC = "asc" as const;
const SORT_DIRECTION_DESC = "desc" as const;
type SortDirection = typeof SORT_DIRECTION_ASC | typeof SORT_DIRECTION_DESC;
type SortableRow = { displayData: string[]; rawData: StartlistExtendedRow };

function allowSelfEdit(plannedStartTimeInMs: number | undefined | null) {
  const now = new Date().getTime();
  const closeSelfEditSignupMsBeforeStart = 30 * 60 * 1000; // 30 min

  return (
    plannedStartTimeInMs == null ||
    now + closeSelfEditSignupMsBeforeStart < plannedStartTimeInMs
  );
}

export function Search(props: {
  competitionTag: string;
  startlist: Array<StartlistExtendedRow>;
  searchString: string;
  headerValues: Array<string>;
  dataMapper: (row: StartlistExtendedRow) => Array<string>;
  setEditRow: (rawData: StartlistExtendedRow) => void;
}) {
  const session = useSessionState();
  const [sortColumn, setSortColumn] = useState<number>(1);
  const [sortDirection, setSortDirection] =
    useState<SortDirection>(SORT_DIRECTION_ASC);

  return (
    <div>
      <Heading size="sm" mb={2}>
        {props.competitionTag}
      </Heading>
      <Box
        key={"searchbox"}
        border="1px"
        borderColor="gray.300"
        p={4}
        rounded="xl"
        overflowY={"auto"}
        width={"fit-content"}
      >
        <Table size="sm">
          {/** Name, Year, Club/Team, signupId, lastChange */}
          <TableHeaders
            sortDirection={sortDirection}
            onClick={(headerIndex) => {
              setSortColumn((oldColumn) => {
                console.log("sortcomumn ", oldColumn, headerIndex);
                if (headerIndex === oldColumn) {
                  setSortDirection((oldDirection) => {
                    return oldDirection === SORT_DIRECTION_ASC
                      ? SORT_DIRECTION_DESC
                      : SORT_DIRECTION_ASC;
                  });
                } else {
                  setSortDirection(SORT_DIRECTION_ASC);
                }
                return headerIndex;
              });
            }}
            headers={["", ...props.headerValues]}
          />
          <Tbody>
            {props.startlist.length === 0 ? (
              <Tr>
                <Td
                  colSpan={props.headerValues.length + 1}
                  textAlign="center"
                  py={6}
                  color="gray.500"
                >
                  Inga anmälningar hittade
                </Td>
              </Tr>
            ) : (
              props.startlist
                .filter(filterRules(props.searchString))
                .map(
                  (r) =>
                    ({
                      displayData: props.dataMapper(r),
                      rawData: r,
                    } as SortableRow)
                )
                //.sort(sortingRules(sortColumn, sortDirection))
                .sort(
                  (a, b) =>
                    (a.rawData.startnumber ?? 0) - (b.rawData.startnumber ?? 0)
                )
                .map((row, i) => {
                  const enableEditing =
                    session.hasAtLeastRole(Role.OFFICIAL) ||
                    allowSelfEdit(row.rawData.startgroupPlannedStartTime);
                  return (
                    <TableRow
                      key={`signuprow_${row.rawData.signedupId}`}
                      rowData={row.displayData}
                      softDeleted={row.rawData.signedupSoftDeleted}
                      onEdit={
                        enableEditing
                          ? () => {
                              props.setEditRow(row.rawData);
                            }
                          : null
                      }
                    />
                  );
                })
            )}
          </Tbody>
        </Table>
      </Box>
    </div>
  );
}

function filterRules(searchText: string) {
  return (row: StartlistExtendedRow) => {
    const search = searchText.trim();
    if (searchText === "") {
      return true;
    }
    return (
      row.disciplineName.includes(search) ||
      (row.startnumber ?? "").toString().includes(search) ||
      `${row.firstname} ${row.lastName}`
        .toLowerCase()
        .includes(search.toLowerCase()) ||
      (row.birthYear?.toString() ?? "").includes(search) ||
      (row.groupName ?? row.sportTeam ?? "")
        .toLowerCase()
        .includes(search.toLowerCase()) ||
      (row.paymentId ?? "").toString().includes(search)
    );
  };
}

function sortingRules(sortColumn: number, sortDirection: "asc" | "desc") {
  return (a: SortableRow, b: SortableRow) => {
    if (a.rawData.disciplineDisplayOrder !== b.rawData.disciplineDisplayOrder) {
      return (
        (sortColumn != 0 || sortDirection === "asc" ? 1 : -1) *
        (a.rawData.disciplineDisplayOrder - b.rawData.disciplineDisplayOrder)
      );
    }

    if (
      (a.rawData.groupName != null || b.rawData.groupName != null) &&
      a.rawData.groupName !== b.rawData.groupName
    ) {
      return a.rawData.groupName?.localeCompare(b.rawData.groupName ?? "") ?? 0;
    }

    const aCell = a.displayData[sortColumn] ?? "";
    const bCell = b.displayData[sortColumn] ?? "";
    if (aCell > bCell) {
      return sortDirection === "asc" ? 1 : -1;
    } else if (aCell < bCell) {
      return sortDirection === "asc" ? -1 : 1;
    }
    return 0;
  };
}

function TableHeaders(props: {
  headers: Array<string>;
  onClick: (headerIndex: number) => void;
  sortDirection: SortDirection;
}) {
  const [headerInFocus, setHeaderInFocus] = useState<number | null>(null);
  return (
    <Thead>
      <Tr key={"header"}>
        {props.headers.map((name, i) => {
          //const isFocused = i === headerInFocus;
          //const icon =
          //isFocused && props.sortDirection === SORT_DIRECTION_ASC ? (
          //<ArrowUp size={16} style={{ display: "inline", marginLeft: 4 }} />
          //) : isFocused && props.sortDirection === SORT_DIRECTION_DESC ? (
          //<ArrowDown
          //size={16}
          //style={{ display: "inline", marginLeft: 4 }}
          ///>
          //) : null;
          return (
            <Th
              key={name}
              cursor={"pointer"}
              onClick={() => {
                console.log(`Picked ${i}`);
                setHeaderInFocus(i);
              }}
            >
              {name} {/*icon*/}
            </Th>
          );
        })}
      </Tr>
    </Thead>
  );
}

function TableRow(props: {
  softDeleted: boolean;
  rowData: (string | number | null | undefined)[];
  onEdit: null | (() => void);
}) {
  return (
    <Tr
      _hover={{ background: "blue.200" }}
      background={props.softDeleted ? "gray.200" : undefined}
      textColor={props.softDeleted ? "gray.600" : undefined}
      style={
        props.softDeleted
          ? {
              textDecoration: "line-through",
            }
          : undefined
      }
    >
      <Td key={`columndata -1`}>
        <Tooltip hidden={!!props.onEdit} label="Klassen är stängd">
          <IconButton
            aria-label="Edit"
            icon={<WrenchIcon />}
            variant="ghost"
            size="sm"
            p={1}
            isDisabled={props.onEdit == null}
            onClick={() => {
              if (props.onEdit) {
                props.onEdit();
              }
            }}
          />
        </Tooltip>
      </Td>
      {props.rowData.map((cell, j) => (
        <Td key={`columndata ${j}`}>{cell}</Td>
      ))}
    </Tr>
  );
}
